<template>
  <div v-if="pageLoading" class="m-3">
    <placeholder-content :is-load="true" />
  </div>
  <div v-else-if="!hasPermission" class="m-3">
    <access-denied />
  </div>
  <div v-else-if="featureUnavailable" class="m-3">
    <feature-not-available back-route="reports" back-text="_plan.Back to reports" />
  </div>
  <div v-else class="p-3">
    <!-- Header -->
    <div class="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-3 gap-2">
      <div class="d-flex align-items-center gap-2">
        <div>
          <h5 class="fw-bold mb-0">
            <i class="fa-solid fa-gift me-2" style="color: #16a34a;"></i>{{ $t('_report.Promotion & Loyalty Performance') }}
          </h5>
          <small class="text-muted">{{ $t('Finance & Payments') }}</small>
        </div>
      </div>
      <div class="d-flex gap-2">
        <button class="btn btn-outline-primary btn-sm" @click="downloadPdf" :disabled="pdfLoading || loading">
          <i class="fa-solid fa-download me-1"></i>{{ $t('Download') }}
        </button>
        <button class="btn btn-primary btn-sm" @click="previewPdf" :disabled="pdfLoading || loading">
          <span v-if="pdfLoading"><i class="fa-solid fa-spinner fa-spin me-1"></i>{{ $t('Generating') }}...</span>
          <span v-else><i class="fa-solid fa-file-pdf me-1"></i>{{ $t('Preview PDF') }}</span>
        </button>
      </div>
    </div>

    <!-- Filters -->
    <div class="card border-0 shadow-sm mb-4">
      <div class="card-body py-2 px-3">
        <div class="d-flex align-items-center gap-2 flex-wrap">
          <select v-model="filters.branch" class="form-select form-select-sm" style="min-width: 140px; max-width: 200px;" :disabled="loading">
            <option value="all">{{ $t('All Branches') }}</option>
            <option v-for="b in branches" :key="b.id" :value="b.id">{{ b.name }}</option>
          </select>
          <div class="input-group input-group-sm w-auto date-range-input-group">
            <span class="input-group-text"><i class="fa-regular fa-calendar"></i></span>
            <date-range-picker
              v-model="filters.date_range"
              :date-range="filters.date_range"
              :locale-data="dateRangeLocale"
              :ranges="dateRanges"
              :opens="'right'"
              :auto-apply="true"
              :show-dropdowns="true"
              :control-container-class="''"
            >
              <template v-slot:input="picker">
                <span v-if="picker.startDate && picker.endDate">
                  {{ formatDate(picker.startDate) }} — {{ formatDate(picker.endDate) }}
                </span>
                <span v-else class="text-muted">{{ $t('Select date range') }}</span>
              </template>
            </date-range-picker>
          </div>
        </div>
      </div>
    </div>

    <!-- Error -->
    <div v-if="errorMessage" class="alert alert-danger d-flex justify-content-between align-items-center" role="alert">
      <span><i class="fa-solid fa-triangle-exclamation me-2"></i>{{ errorMessage }}</span>
      <button class="btn btn-sm btn-outline-danger" @click="loadReport">{{ $t('Retry') }}</button>
    </div>

    <div :class="{ 'report-loading': loading }">

    <!-- KPI Cards -->
    <div class="row g-3 mb-4">
      <div class="col-6 col-md-3">
        <div class="card border-0 shadow-sm h-100">
          <div class="card-body p-3 text-center">
            <div class="text-muted small mb-1">{{ $t('_report.Promotion redemptions') }}</div>
            <div class="fw-bold fs-4 text-success">{{ summary.promotion_redemptions || 0 }}</div>
          </div>
        </div>
      </div>
      <div class="col-6 col-md-3">
        <div class="card border-0 shadow-sm h-100">
          <div class="card-body p-3 text-center">
            <div class="text-muted small mb-1">{{ $t('_report.Promo reward cost') }}</div>
            <div class="fw-bold fs-4 text-danger">{{ formatCurrency(summary.promotion_reward_cost) }}</div>
          </div>
        </div>
      </div>
      <div class="col-6 col-md-3">
        <div class="card border-0 shadow-sm h-100">
          <div class="card-body p-3 text-center">
            <div class="text-muted small mb-1">{{ $t('_report.Influenced sales') }}</div>
            <div class="fw-bold fs-4 text-primary">{{ formatCurrency(summary.promotion_influenced_sales) }}</div>
          </div>
        </div>
      </div>
      <div class="col-6 col-md-3">
        <div class="card border-0 shadow-sm h-100">
          <div class="card-body p-3 text-center">
            <div class="text-muted small mb-1">{{ $t('_report.Loyalty points spent') }}</div>
            <div class="fw-bold fs-4">{{ (summary.loyalty_points_spent || 0).toLocaleString() }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Promotions table -->
    <div class="card border-0 shadow-sm mb-4">
      <div class="card-header bg-white fw-semibold py-2">
        <i class="fa-solid fa-bullhorn me-2 text-success"></i>{{ $t('_report.By promotion') }}
      </div>
      <div class="card-body p-0">
        <div class="table-responsive">
          <table class="table table-hover table-sm mb-0 align-middle">
            <thead class="table-light">
              <tr>
                <th class="ps-3">{{ $t('_promotion.Promotion') }}</th>
                <th class="text-end">{{ $t('_report.Redemptions') }}</th>
                <th class="text-end">{{ $t('_report.Reward cost') }}</th>
                <th class="text-end">{{ $t('_report.PWP revenue') }}</th>
                <th class="text-end">{{ $t('_report.Bills') }}</th>
                <th class="text-end pe-3">{{ $t('_report.Influenced sales') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="!promotions.length && !loading">
                <td colspan="6" class="text-center text-muted py-4">{{ $t('No data') }}</td>
              </tr>
              <tr v-for="p in promotions" :key="p.promotion_id">
                <td class="ps-3 fw-medium">{{ $t(p.name) }}</td>
                <td class="text-end">{{ p.redemptions }}</td>
                <td class="text-end text-danger">-{{ formatCurrency(p.reward_cost) }}</td>
                <td class="text-end">{{ formatCurrency(p.pwp_revenue) }}</td>
                <td class="text-end">{{ p.bills }}</td>
                <td class="text-end pe-3 fw-semibold text-primary">{{ formatCurrency(p.influenced_sales) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Loyalty rewards table -->
    <div class="card border-0 shadow-sm mb-4">
      <div class="card-header bg-white fw-semibold py-2">
        <i class="fa-solid fa-star me-2 text-warning"></i>{{ $t('_report.By loyalty reward') }}
      </div>
      <div class="card-body p-0">
        <div class="table-responsive">
          <table class="table table-hover table-sm mb-0 align-middle">
            <thead class="table-light">
              <tr>
                <th class="ps-3">{{ $t('_loyalty.Reward') }}</th>
                <th>{{ $t('_report.Type') }}</th>
                <th class="text-end">{{ $t('_report.Redemptions') }}</th>
                <th class="text-end">{{ $t('_report.Points spent') }}</th>
                <th class="text-end">{{ $t('_report.Reward cost') }}</th>
                <th class="text-end pe-3">{{ $t('_report.Discount given') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="!loyalty.length && !loading">
                <td colspan="6" class="text-center text-muted py-4">{{ $t('No data') }}</td>
              </tr>
              <tr v-for="r in loyalty" :key="r.reward_id">
                <td class="ps-3 fw-medium">{{ $t(r.name) }}</td>
                <td>
                  <span class="badge" :class="r.reward_type === 'free_product' ? 'bg-success-subtle text-success' : 'bg-info-subtle text-info'">
                    {{ r.reward_type === 'free_product' ? $t('_report.Free product') : $t('_report.Discount') }}
                  </span>
                </td>
                <td class="text-end">{{ r.redemptions }}</td>
                <td class="text-end">{{ (r.points_spent || 0).toLocaleString() }}</td>
                <td class="text-end text-danger">{{ r.reward_cost > 0 ? '-' + formatCurrency(r.reward_cost) : '–' }}</td>
                <td class="text-end pe-3 text-danger">{{ r.discount_given > 0 ? '-' + formatCurrency(r.discount_given) : '–' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    </div><!-- /loading wrapper -->

    <div v-if="loading" class="report-loading-overlay">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">{{ $t('Loading') + '...' }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import { Reports } from '@/services/endpoint/reports.service';
import reportMixin from './reportMixin';

export default {
  name: 'ReportPromotionPerformance',
  mixins: [reportMixin],
  data() {
    return {
      reportLabel: 'Promotion & Loyalty Performance',
      summary: {
        promotion_redemptions: 0, promotion_reward_cost: 0, promotion_influenced_sales: 0,
        loyalty_redemptions: 0, loyalty_points_spent: 0, loyalty_reward_cost: 0, loyalty_discount_given: 0,
      },
      promotions: [],
      loyalty: [],
    };
  },
  methods: {
    async fetchReport() {
      const res = await Reports.promotionPerformance(this.buildFetchParams());
      const payload = res?.data?.data || {};
      const filter = res?.data?.filter || {};
      this.branches = filter.branches || [];
      this.summary = payload.summary || this.summary;
      this.promotions = payload.promotions || [];
      this.loyalty = payload.loyalty || [];
    },

    async buildPdfDefinition() {
      const content = [
        ...this.buildPdfHeader(),
        { text: this.$t('_report.Promotion & Loyalty Performance'), style: 'sectionTitle' },
        {
          columns: [
            { text: [{ text: this.$t('_report.Promotion redemptions') + '\n', style: 'kpiLabel' }, { text: String(this.summary.promotion_redemptions || 0), style: 'kpiValue' }], width: '*' },
            { text: [{ text: this.$t('_report.Promo reward cost') + '\n', style: 'kpiLabel' }, { text: this.formatCurrency(this.summary.promotion_reward_cost), style: 'kpiValue' }], width: '*' },
            { text: [{ text: this.$t('_report.Influenced sales') + '\n', style: 'kpiLabel' }, { text: this.formatCurrency(this.summary.promotion_influenced_sales), style: 'kpiValue' }], width: '*' },
            { text: [{ text: this.$t('_report.Loyalty points spent') + '\n', style: 'kpiLabel' }, { text: String(this.summary.loyalty_points_spent || 0), style: 'kpiValue' }], width: '*' },
          ],
          margin: [0, 0, 0, 16],
        },
        { text: this.$t('_report.By promotion'), style: 'sectionTitle' },
        {
          table: {
            headerRows: 1,
            widths: ['*', 'auto', 'auto', 'auto', 'auto', 'auto'],
            body: [
              [
                { text: this.$t('_promotion.Promotion'), style: 'tableHeader' },
                { text: this.$t('_report.Redemptions'), style: 'tableHeader', alignment: 'right' },
                { text: this.$t('_report.Reward cost'), style: 'tableHeader', alignment: 'right' },
                { text: this.$t('_report.PWP revenue'), style: 'tableHeader', alignment: 'right' },
                { text: this.$t('_report.Bills'), style: 'tableHeader', alignment: 'right' },
                { text: this.$t('_report.Influenced sales'), style: 'tableHeader', alignment: 'right' },
              ],
              ...this.promotions.map(p => [
                { text: this.$t(p.name), style: 'tableCell' },
                { text: String(p.redemptions), style: 'tableCell', alignment: 'right' },
                { text: `-${this.formatCurrencyRaw(p.reward_cost)}`, style: 'tableCell', alignment: 'right', color: '#ef4444' },
                { text: this.formatCurrencyRaw(p.pwp_revenue), style: 'tableCell', alignment: 'right' },
                { text: String(p.bills), style: 'tableCell', alignment: 'right' },
                { text: this.formatCurrencyRaw(p.influenced_sales), style: 'tableCell', alignment: 'right' },
              ]),
            ],
          },
          layout: 'lightHorizontalLines',
          margin: [0, 0, 0, 16],
        },
        { text: this.$t('_report.By loyalty reward'), style: 'sectionTitle' },
        {
          table: {
            headerRows: 1,
            widths: ['*', 'auto', 'auto', 'auto', 'auto', 'auto'],
            body: [
              [
                { text: this.$t('_loyalty.Reward'), style: 'tableHeader' },
                { text: this.$t('_report.Type'), style: 'tableHeader' },
                { text: this.$t('_report.Redemptions'), style: 'tableHeader', alignment: 'right' },
                { text: this.$t('_report.Points spent'), style: 'tableHeader', alignment: 'right' },
                { text: this.$t('_report.Reward cost'), style: 'tableHeader', alignment: 'right' },
                { text: this.$t('_report.Discount given'), style: 'tableHeader', alignment: 'right' },
              ],
              ...this.loyalty.map(r => [
                { text: this.$t(r.name), style: 'tableCell' },
                { text: r.reward_type === 'free_product' ? this.$t('_report.Free product') : this.$t('_report.Discount'), style: 'tableCell' },
                { text: String(r.redemptions), style: 'tableCell', alignment: 'right' },
                { text: String(r.points_spent || 0), style: 'tableCell', alignment: 'right' },
                { text: r.reward_cost > 0 ? `-${this.formatCurrencyRaw(r.reward_cost)}` : '-', style: 'tableCell', alignment: 'right' },
                { text: r.discount_given > 0 ? `-${this.formatCurrencyRaw(r.discount_given)}` : '-', style: 'tableCell', alignment: 'right' },
              ]),
            ],
          },
          layout: 'lightHorizontalLines',
        },
      ];
      return { content };
    },
  },
};
</script>

<style scoped>
.report-loading {
  opacity: 0.5;
  pointer-events: none;
  transition: opacity 0.2s;
}
.report-loading-overlay {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1050;
}
</style>
